/**
 * What a contact has been worth to us — docs/BUSINESS-RULES.md.
 *
 * Three figures, not one (Jonathon, 2026-08-04): what they have paid us, what
 * we kept, and the margin between. A contact who spends a lot at a thin margin
 * and one who spends less at a healthy one look identical under a single
 * "lifetime value" number, and they are not the same customer.
 *
 * Only approved work counts. A lead that never signed is not value, and a job
 * written off later stops counting the moment it is marked lost — otherwise the
 * figure would keep crediting a contact for money that never arrived.
 *
 * Every number here comes from `computeLadder`, which is the only thing allowed
 * to say what a job is worth. This module adds them up; it does not re-derive
 * them.
 */

import type { LadderTotals } from "./ladder.js";
import { countsAsRealWork } from "./lost.js";
import { addCents, cents, ratio, type Cents } from "./money.js";
import type { Job } from "./types.js";

/**
 * Does this job count toward the contact's lifetime figures?
 *
 * Approved means handed to production — `sends_at` is stamped — which covers
 * jobs still being built as well as finished ones. A lost job never counts,
 * even if it was approved before it fell over, and neither does a test job:
 * see `countsAsRealWork`.
 */
export const countsTowardLifetime = (
  job: Pick<Job, "sends_at" | "stage"> & { is_test?: boolean | null },
): boolean => Boolean(job.sends_at) && countsAsRealWork(job);

export interface LifetimeValue {
  /** Contract dollars — the grand total of every qualifying job. */
  revenue: Cents;
  /** What was left after what the work cost. */
  grossProfit: Cents;
  /** grossProfit / revenue. Zero when there is no revenue to divide by. */
  margin: number;
  /** Jobs behind the money — approved, and not written off. */
  approvedJobs: number;
  /** Every job this contact has ever had, won or lost. */
  totalJobs: number;
  /**
   * approvedJobs / totalJobs. How often this contact actually buys.
   *
   * Deliberately this way up. Jonathon described it as total ÷ approved, which
   * gives 5 for a contact who signed one job in five — a ratio, not a rate, and
   * it would print as 500%.
   */
  conversionRate: number;
  /** Revenue ÷ approved jobs. What a job with this contact is typically worth. */
  averageJobSize: Cents;
}

/**
 * Where a conversion rate stops being a worry.
 *
 * Jonathon, 2026-08-04: at or above this it reads green, below it red. It is a
 * judgement about this business, not a statistic, which is why it is a named
 * constant rather than a number buried in a component.
 */
export const HEALTHY_CONVERSION_RATE = 0.25;

export const emptyLifetimeValue = (): LifetimeValue => ({
  revenue: cents(0),
  grossProfit: cents(0),
  margin: 0,
  approvedJobs: 0,
  totalJobs: 0,
  conversionRate: 0,
  averageJobSize: cents(0),
});

/**
 * Add up what a contact has been worth.
 *
 * The margin is taken on the totals rather than averaged across jobs: a $40k
 * job at 30% and a $2k job at 60% is not a 45% customer.
 */
export function lifetimeValue(
  totals: readonly Pick<LadderTotals, "grandTotal" | "grossProfit">[],
  counts: { approved: number; total: number },
): LifetimeValue {
  const revenue = addCents(...totals.map((t) => t.grandTotal));
  const grossProfit = addCents(...totals.map((t) => t.grossProfit));

  return {
    revenue,
    grossProfit,
    margin: ratio(grossProfit, revenue),
    approvedJobs: counts.approved,
    totalJobs: counts.total,
    conversionRate: counts.total > 0 ? counts.approved / counts.total : 0,
    averageJobSize:
      counts.approved > 0
        ? cents(Math.round(revenue / counts.approved))
        : cents(0),
  };
}
